/**
 * Valnexo CRM - Academy Supervision Dashboard (VP)
 * ==================================================
 * Dashboard de supervision réservé aux VP :
 * - Suivi de la progression formation de leurs équipes
 * - Filtre par équipe
 * - Inclut le VP lui-même + ses apporteurs
 * - Respect strict du RBAC (un VP ne voit que ses équipes)
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
    Users, BookOpen, Award, Clock, ChevronDown, ChevronUp, Search,
    BarChart3, ArrowLeft, TrendingUp, Target, Timer, ShieldCheck, UserCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';

const supervisionAPI = {
    getDashboard: (teamId) => api.get('/academy/vp/dashboard', { params: teamId ? { team_id: teamId } : {} }),
};

function formatTime(seconds) {
    if (!seconds || seconds <= 0) return '0min';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`;
    return `${m}min`;
}

function ScoreGauge({ score, size = 'md' }) {
    const radius = size === 'lg' ? 40 : 28;
    const stroke = size === 'lg' ? 6 : 4;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const dim = (radius + stroke) * 2;
    const color = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';

    return (
        <div className="relative inline-flex items-center justify-center">
            <svg width={dim} height={dim} className="-rotate-90">
                <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
                <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke={color} strokeWidth={stroke}
                    strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" />
            </svg>
            <span className={cn(
                "absolute font-bold",
                size === 'lg' ? 'text-lg' : 'text-xs',
                score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-rose-600'
            )}>
                {score}
            </span>
        </div>
    );
}

function UserRow({ stat, modulesCount, isSelf }) {
    const [expanded, setExpanded] = useState(false);
    const u = stat.user;
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
    const roleLabels = { admin_full: 'Admin', admin: 'Admin', vp: 'VP', apporteur: 'Apporteur', comptable: 'Comptable' };

    return (
        <>
            <tr
                className={cn(
                    "hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100",
                    isSelf && "bg-yellow-50/40 hover:bg-yellow-50/70"
                )}
                onClick={() => setExpanded(!expanded)}
                data-testid={`vp-user-row-${u.id}`}
            >
                <td className="p-3">
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs",
                            isSelf ? "bg-gradient-to-br from-indigo-500 to-purple-600" : "bg-gradient-to-br from-yellow-400 to-orange-500"
                        )}>
                            {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                        </div>
                        <div>
                            <p className="font-medium text-slate-900 text-sm flex items-center gap-2">
                                {name}
                                {isSelf && (
                                    <Badge variant="outline" className="text-[10px] py-0 h-4 border-indigo-200 text-indigo-700 bg-indigo-50">
                                        <UserCircle2 className="w-2.5 h-2.5 mr-0.5" /> Moi
                                    </Badge>
                                )}
                            </p>
                            <p className="text-xs text-slate-500">{roleLabels[u.role] || u.role}</p>
                        </div>
                    </div>
                </td>
                <td className="p-3 text-center">
                    <ScoreGauge score={stat.global_score} />
                </td>
                <td className="p-3 text-center">
                    <span className="text-sm font-medium">{stat.modules_completed}/{stat.modules_total ?? modulesCount}</span>
                    <Progress value={stat.completion_rate} className="h-1.5 mt-1 w-16 mx-auto" />
                </td>
                <td className="p-3 text-center">
                    <span className={cn(
                        "text-sm font-semibold",
                        stat.average_quiz_score >= 80 ? 'text-emerald-600' : stat.average_quiz_score >= 50 ? 'text-amber-600' : 'text-rose-600'
                    )}>
                        {stat.average_quiz_score > 0 ? `${stat.average_quiz_score}%` : '—'}
                    </span>
                </td>
                <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-sm text-slate-600">
                        <Timer className="w-3.5 h-3.5" />
                        {stat.total_time_formatted}
                    </div>
                </td>
                <td className="p-3 text-center">
                    {expanded ? <ChevronUp className="w-4 h-4 mx-auto text-slate-400" /> : <ChevronDown className="w-4 h-4 mx-auto text-slate-400" />}
                </td>
            </tr>
            {expanded && stat.modules_detail?.length > 0 && (
                <tr>
                    <td colSpan={6} className="p-0">
                        <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {stat.modules_detail.map((md) => (
                                    <div key={md.module_id} className={cn(
                                        "p-3 rounded-lg border bg-white",
                                        md.quiz_passed ? 'border-emerald-200' : md.quiz_score !== null ? 'border-amber-200' : 'border-slate-200'
                                    )}>
                                        <p className="font-medium text-sm text-slate-800 truncate">{md.module_title}</p>
                                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
                                            <span className="flex items-center gap-1">
                                                <Target className="w-3 h-3" />
                                                {md.progress_percent}%
                                            </span>
                                            {md.quiz_score !== null && (
                                                <span className={cn(
                                                    "flex items-center gap-1 font-semibold",
                                                    md.quiz_passed ? 'text-emerald-600' : 'text-rose-500'
                                                )}>
                                                    <Award className="w-3 h-3" />
                                                    Quiz: {md.quiz_score}%
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatTime(md.time_spent_seconds)}
                                            </span>
                                        </div>
                                        {md.quiz_attempts > 0 && (
                                            <p className="text-xs text-slate-500 mt-1">{md.quiz_attempts} tentative{md.quiz_attempts > 1 ? 's' : ''}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default function AcademySupervision() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('users');
    const [selectedTeamId, setSelectedTeamId] = useState('all');

    useEffect(() => {
        if (user?.role !== 'vp') return;
        loadDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTeamId, user?.role]);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const teamId = selectedTeamId === 'all' ? null : selectedTeamId;
            const res = await supervisionAPI.getDashboard(teamId);
            setDashboard(res.data);
        } catch (err) {
            if (err?.response?.status === 403) {
                toast.error('Accès réservé aux VP');
            } else {
                toast.error('Erreur chargement du dashboard');
            }
        } finally {
            setLoading(false);
        }
    };

    // Garde-fou côté front : si l'utilisateur n'est pas VP, on redirige vers l'Académie
    if (user && user.role !== 'vp') {
        return (
            <DashboardLayout>
                <div className="p-6 text-center" data-testid="supervision-access-denied">
                    <ShieldCheck className="w-10 h-10 mx-auto text-slate-300" />
                    <p className="mt-3 text-slate-600">Ce dashboard est réservé aux VP (chefs d'équipe).</p>
                    <Button variant="outline" onClick={() => navigate('/academy')} className="mt-4" data-testid="supervision-back-academy">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Retour à l'Académie
                    </Button>
                </div>
            </DashboardLayout>
        );
    }

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6 p-6">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </DashboardLayout>
        );
    }

    if (!dashboard) return null;

    const { overview, users_stats, modules_stats, teams_list } = dashboard;
    const filteredUsers = users_stats.filter(s => {
        if (!search) return true;
        const name = `${s.user.first_name} ${s.user.last_name} ${s.user.email}`.toLowerCase();
        return name.includes(search.toLowerCase());
    });

    const validQuizUsers = users_stats.filter(s => s.average_quiz_score > 0);
    const avgQuiz = validQuizUsers.length > 0
        ? Math.round(validQuizUsers.reduce((a, b) => a + b.average_quiz_score, 0) / validQuizUsers.length)
        : 0;
    const avgGlobal = users_stats.length > 0
        ? Math.round(users_stats.reduce((a, b) => a + b.global_score, 0) / users_stats.length)
        : 0;

    return (
        <DashboardLayout>
            <div className="space-y-6 p-4 md:p-6" data-testid="academy-supervision-dashboard">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/academy')} data-testid="back-to-academy">
                            <ArrowLeft className="w-4 h-4 mr-1" />
                            Retour
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                                <ShieldCheck className="w-6 h-6 text-indigo-600" />
                                Dashboard Supervision
                            </h1>
                            <p className="text-sm text-slate-500">
                                Suivez la progression formation de vos équipes
                            </p>
                        </div>
                    </div>

                    {/* Filtre par équipe */}
                    {teams_list && teams_list.length > 0 && (
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">Équipe :</span>
                            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                                <SelectTrigger className="w-[260px]" data-testid="team-filter">
                                    <SelectValue placeholder="Toutes mes équipes" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all" data-testid="team-filter-all">
                                        Toutes mes équipes ({teams_list.length})
                                    </SelectItem>
                                    {teams_list.map(t => (
                                        <SelectItem key={t.id} value={t.id} data-testid={`team-filter-${t.id}`}>
                                            {t.name} · {t.members_count} membre{t.members_count > 1 ? 's' : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-blue-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-50 rounded-lg"><BookOpen className="w-5 h-5 text-blue-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold" data-testid="kpi-published-modules">{overview.published_modules}</p>
                                    <p className="text-xs text-slate-500">Modules publiés</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-emerald-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-50 rounded-lg"><Users className="w-5 h-5 text-emerald-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold" data-testid="kpi-users-count">{overview.total_users}</p>
                                    <p className="text-xs text-slate-500">Membres suivis</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-yellow-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-yellow-50 rounded-lg"><TrendingUp className="w-5 h-5 text-yellow-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold" data-testid="kpi-avg-global">{avgGlobal}</p>
                                    <p className="text-xs text-slate-500">Score moyen /100</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-purple-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-50 rounded-lg"><BarChart3 className="w-5 h-5 text-purple-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold" data-testid="kpi-avg-quiz">{avgQuiz}%</p>
                                    <p className="text-xs text-slate-500">Moy. quiz</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
                            <Users className="w-4 h-4" /> Mon équipe
                        </TabsTrigger>
                        <TabsTrigger value="modules" className="gap-2" data-testid="tab-modules">
                            <BookOpen className="w-4 h-4" /> Modules
                        </TabsTrigger>
                    </TabsList>

                    {/* Tab Utilisateurs */}
                    <TabsContent value="users">
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between gap-4">
                                    <CardTitle className="text-base">Progression par membre</CardTitle>
                                    <div className="relative w-64">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                            placeholder="Rechercher..."
                                            className="pl-9 h-9"
                                            data-testid="search-users"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                                <th className="p-3 text-left font-medium">Membre</th>
                                                <th className="p-3 text-center font-medium">Note /100</th>
                                                <th className="p-3 text-center font-medium">Modules</th>
                                                <th className="p-3 text-center font-medium">Moy. Quiz</th>
                                                <th className="p-3 text-center font-medium">Temps actif</th>
                                                <th className="p-3 text-center font-medium w-12"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.map(stat => (
                                                <UserRow
                                                    key={stat.user.id}
                                                    stat={stat}
                                                    modulesCount={overview.published_modules}
                                                    isSelf={stat.user.id === user?.id}
                                                />
                                            ))}
                                            {filteredUsers.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="text-center py-8 text-slate-500" data-testid="no-users-result">
                                                        Aucun membre dans ce périmètre
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab Modules */}
                    <TabsContent value="modules">
                        <div className="grid gap-4">
                            {modules_stats.length === 0 && (
                                <Card>
                                    <CardContent className="p-8 text-center text-slate-500" data-testid="no-modules-result">
                                        Aucune statistique disponible
                                    </CardContent>
                                </Card>
                            )}
                            {modules_stats.map(ms => (
                                <Card key={ms.module.id} data-testid={`module-stat-${ms.module.id}`}>
                                    <CardContent className="p-5">
                                        <div className="flex items-center justify-between flex-wrap gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-semibold text-slate-900 truncate">{ms.module.title}</h3>
                                                    <Badge variant={ms.module.is_published ? "default" : "secondary"} className="text-xs shrink-0">
                                                        {ms.module.is_published ? 'Publié' : 'Brouillon'}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-slate-500">{ms.module.category}</p>
                                            </div>
                                            <div className="flex items-center gap-6 text-sm">
                                                <div className="text-center">
                                                    <p className="font-bold text-lg text-slate-900">{ms.users_started}</p>
                                                    <p className="text-xs text-slate-500">Commencé</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-bold text-lg text-emerald-600">{ms.users_completed}</p>
                                                    <p className="text-xs text-slate-500">Terminé</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-bold text-lg text-blue-600">{ms.completion_rate}%</p>
                                                    <p className="text-xs text-slate-500">Taux</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className={cn(
                                                        "font-bold text-lg",
                                                        ms.average_quiz_score >= 80 ? 'text-emerald-600' : ms.average_quiz_score >= 50 ? 'text-amber-600' : 'text-rose-600'
                                                    )}>
                                                        {ms.average_quiz_score > 0 ? `${ms.average_quiz_score}%` : '—'}
                                                    </p>
                                                    <p className="text-xs text-slate-500">Moy. Quiz</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-bold text-lg text-slate-700">{formatTime(ms.average_time_seconds)}</p>
                                                    <p className="text-xs text-slate-500">Temps moy.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
}
